import { type Context, type Message, type State } from '@ag-ui/core';

import { type AiModelConfig } from '../api/aimodels/useAiModels';
import { type CanvasTokenNode } from '../store/slices/createCanvasTokenNodeSlice';
import { type ToolCallResultEventParams } from './callbacks';

export type MessagesChangedPayload = {
  messages: Message[];
  threadId: string;
};

export type StateChangedPayload = {
  state: State;
  threadId: string;
};

// biome-ignore lint/style/noMagicNumbers: variant-count enumeration
export const VARIANT_COUNTS = [1, 2, 3, 4] as const;

export type VariantCount = (typeof VARIANT_COUNTS)[number];

export const DEFAULT_VARIANT_COUNT: VariantCount = 3;

export type VariantCountSource = 'composer_pill' | 'toolbar_pill';

export type RequestedVariantCount = { count: VariantCount; source: VariantCountSource };

export type ProgrammaticMessageOptions = {
  actionName?:
    | 'cameraAngle'
    | 'cameraMotion'
    | 'changeLighting'
    | 'editElements'
    | 'editTextOcr'
    | 'extendBackground'
    | 'extractLayers'
    | 'generateImage'
    | 'imageToVector'
    | 'imageToVideo'
    | 'localizationOcr'
    | 'localizationTranslate'
    | 'prompt'
    | 'removeBackground'
    | 'removeObject'
    | 'smartResize'
    | 'translate'
    | 'upscale'
    | 'videoExtend'
    | 'videoSmartResize'
    | 'videoToGif';

  /**
   * Force a specific generative model for this call only, overriding the canvas-wide
   * model selection. Use for transforms that require a particular model (e.g. spatial
   * edits like cameraAngle/changeLighting that depend on Nano Banana 2's reasoning).
   */
  modelOverride?: AiModelConfig;

  /** Format ids selected in the video smart resize dropdown, in generation order. */
  smartResizeFormatIds?: string[];

  /**
   * Replace the source image in place when `file_edit_complete` returns a single
   * image, even if that source is not in a group. Crop-and-extend uses this so
   * the intersection crop becomes the outpaint result instead of leaving both.
   */
  replaceSourceNode?: boolean;

  sourceNode?: CanvasTokenNode;
  sourceNodeId?: string;

  /**
   * Ordered transform targets for a joined programmatic message. ToolCallResultWatcher maps
   * results to ids in order, whether they arrive in one `file_edit_complete` or one per id.
   */
  transformTargetNodeIds?: readonly string[];

  /** Toolbar path only. */
  variantCount?: VariantCount;
};

export type ToolResponseDismissedPayload = {
  action: 'dismiss';
};

export type ToolResponseCancelPayload = {
  action: 'cancel';
};

export type ToolResponseSkipPayload = {
  action: 'skip';
};

export type ToolResponseCreatedContextField = {
  id: string;
  name: string;
  valueType: 'file' | 'color' | 'font';
};

export type ToolResponseSaveSelectedAssetsPayload = {
  action: 'save';
  createdField?: ToolResponseCreatedContextField;
  selectedAssetIds: string[];
};

export type ToolResponseSaveSelectedFontPayload = {
  action: 'save';
  createdContextSourceValue?: unknown;
  createdField?: ToolResponseCreatedContextField;
  selectedFont: {
    family: string;
    key: string;
    source: string;
    variant: string;
  };
};

export type ToolResponseSaveSelectedColorPayload = {
  action: 'save';
  createdContextSourceValue?: unknown;
  createdField?: ToolResponseCreatedContextField;
  selectedColor: string;
};

export type ToolResponseUploadFontPayload = {
  action: 'upload';
  font: {
    family: string;
    source: 'file';
    variant?: string;
  };
  url: string;
};

export type ToolResponseUploadImagePayload = {
  action: 'upload';
  url: string;
};

export type ToolResponseUploadCreatedValuePayload = {
  action: 'upload';
  createdContextSourceValue: unknown;
  createdField?: ToolResponseCreatedContextField;
};

export type ToolResponseCreateBrandKitPayload = {
  action: 'create';
  contextSourceId: string;
  createdContextSource?: unknown;
  /** Set only during a regenerate flow — the id of the Brand Kit that was deleted. */
  replacedContextSourceId?: string;
  sourceDomain: string;
};

export type ToolResponsePayload =
  | ToolResponseCancelPayload
  | ToolResponseCreateBrandKitPayload
  | ToolResponseDismissedPayload
  | ToolResponseSkipPayload
  | ToolResponseSaveSelectedAssetsPayload
  | ToolResponseSaveSelectedColorPayload
  | ToolResponseSaveSelectedFontPayload
  | ToolResponseUploadCreatedValuePayload
  | ToolResponseUploadFontPayload
  | ToolResponseUploadImagePayload;

export type SendMessageParams = {
  context?: Context[];
  messages: Message[];
  programmaticOptions?: ProgrammaticMessageOptions;
  threadId: string;
  variantCount?: RequestedVariantCount;
};

export type ToolCallResultPayload = {
  programmaticOptions?: ProgrammaticMessageOptions;
  threadId: string;
} & ToolCallResultEventParams;

export type MessagesChangedListener = (payload: MessagesChangedPayload) => void;

export type StateChangedListener = (payload: StateChangedPayload) => void;

export type ToolCallResultListener = (payload: ToolCallResultPayload) => void;

/**
 * How a run ended, classified by the manager at settle time:
 * - `finished` — RUN_FINISHED was received; the deliverable is complete even if the stream
 *   tail had to be force-closed.
 * - `errored` — a genuine terminal error surfaced (server RUN_ERROR or stream failure).
 * - `aborted` — the user stopped the run (cancelRun).
 * - `abandoned` — transport failure: the run ended without any terminal event, either
 *   force-abandoned by the idle watchdog or spontaneously truncated (early EOF).
 */
export type RunSettledOutcome = 'abandoned' | 'aborted' | 'errored' | 'finished';

/** What prompted the idle-watchdog check that declared the run stalled. */
export type StallTrigger = 'reconnect' | 'tab_return' | 'timer';

type RunLifecycleBasePayload = {
  threadId: string;
  runId?: string;
  /** Transform action name from the originating programmatic send; absent for composer sends. */
  actionName?: string;
  /** Resolved source-node id (explicit id, falling back to the source node's group id); absent for composer sends. */
  sourceNodeId?: string;
};

/**
 * Run-lifecycle conclusions, emitted by the manager's run-health accounting:
 * - `started` — RUN_STARTED received.
 * - `stalled` — the idle watchdog declared the stream dead and is force-abandoning the run.
 * - `settled` — the run ended (EOF, error, abort, or forced abandon), with its outcome
 *   classified. A run that never emitted RUN_STARTED settles silently (no payload) — it
 *   never became a run.
 */
export type RunLifecyclePayload =
  | (RunLifecycleBasePayload & { phase: 'started' })
  | (RunLifecycleBasePayload & {
      phase: 'stalled';
      elapsedMs: number;
      resultArrived: boolean;
      runFinishedReceived: boolean;
      trigger: StallTrigger;
    })
  | (RunLifecycleBasePayload & {
      phase: 'settled';
      durationMs: number;
      outcome: RunSettledOutcome;
      resultArrived: boolean;
      runFinishedReceived: boolean;
      requestedVariantCount: VariantCount | undefined;
      variantCountSource: VariantCountSource | undefined;
      deliveredVariantCount: number;
    });

export type RunLifecyclePhase = RunLifecyclePayload['phase'];

export type RunLifecycleListener = (payload: RunLifecyclePayload) => void;

export type FocusNodePayload = { runId: string };

export type FocusNodeListener = (payload: FocusNodePayload) => void;

export type HighlightNodePayload = { mentionItemId: string | null; mentionItemName?: string };

export type HighlightNodeListener = (payload: HighlightNodePayload) => void;

export type GetAuthToken = (options?: { forceRefresh?: boolean }) => Promise<string>;

export type SubscriptionOptions = {
  threadId?: string;
};

export type ListenerEntry<T> = {
  listener: T;
  threadId?: string;
};
